import {LitElement, html} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import {Service} from '../../service/service.js';
import {ElementUtil} from '../../utils/elementUtil.js';
import {TimeUtil} from '../../utils/timeUtil.js';
import {StringUtil} from '../../utils/stringUtil.js';
import {ExportUtil} from '../../utils/exportUtil.js';
import {BaseComponent} from '../basecomponent.js';


/* global customElements */
class SystemStatsGrid extends BaseComponent {
	
	static get properties() {
		return {			
		};
	}
	
	constructor() {
		super();
						
		var statGrid = this;
		setTimeout(function(){
			statGrid.init();
		});
	}
	
	render() {	
		return html`
			<slot></slot>		
		`;
	}
	
	init() {
		var statGrid = this;

		var CHART_TYPE_BAR = 'bar';
		var CHART_TYPE_LINE = 'line';
		var CHART_TYPE_FILL_LINE = 'fillLine';
		var TOTAL_LABEL = 'Total';
		var EXCLUDED_STAT_KEYS = {
			statsPeriodStartMs: true,
			statsPeriodEndMs: true
		};

		var DATASET_COLORS = [
			'#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F',
			'#EDC948', '#B07AA1', '#FF9DA7', '#9C755F', '#BAB0AC'
		];

		var buildCurrentFilter = function(pageRange, backingGrid) {
			var filterType = ElementUtil.query(toolbar, 'filterType');
			var startFilterDts = TimeUtil.startOfDayCompanyTime(ElementUtil.query(toolbar, 'filterStartDate').value);
			var endFilterDts = TimeUtil.endOfDayCompanyTime(ElementUtil.query(toolbar, 'filterEndDate').value);

			return {
				offset: pageRange.offset,
				max: pageRange.max,
				sortField: backingGrid.currentSortField ? backingGrid.currentSortField : 'updateDts',
				sortOrder: backingGrid.currentSortDirection ? backingGrid.currentSortDirection : 'ASC',
				start: startFilterDts,
				end: endFilterDts,
				dateField: 'eventTime',
				filterField: 'type',
				filterValue: filterType.value
			};
		};

		var parseStatValue = function(value) {
			var numberValue = value != null ? Number(value) : 0;
			if (isNaN(numberValue)) {
				return 0;
			}
			return numberValue;
		};

		var collectStatKeys = function(rows) {
			var keys = [];
			var seen = {};
			(rows || []).forEach(function(row) {
				var stats = row.stats;
				if (!stats) {
					return;
				}
				Object.keys(stats).forEach(function(key) {
					if (EXCLUDED_STAT_KEYS[key]) {
						return;
					}
					if (!seen[key]) {
						seen[key] = true;
						keys.push(key);
					}
				});
			});
			return keys;
		};

		var buildPageAggregates = function(rows) {
			var statKeys = collectStatKeys(rows);
			var totalsByNode = {};
			var grandTotals = {};

			statKeys.forEach(function(key) {
				grandTotals[key] = 0;
			});

			(rows || []).forEach(function(row) {
				var nodeId = row.nodeId || '(None)';
				if (!totalsByNode[nodeId]) {
					totalsByNode[nodeId] = {};
					statKeys.forEach(function(key) {
						totalsByNode[nodeId][key] = 0;
					});
				}
				var stats = row.stats || {};
				statKeys.forEach(function(key) {
					var value = parseStatValue(stats[key]);
					totalsByNode[nodeId][key] += value;
					grandTotals[key] += value;
				});
			});

			return {
				statKeys: statKeys,
				nodeIds: Object.keys(totalsByNode).sort(),
				totalsByNode: totalsByNode,
				grandTotals: grandTotals
			};
		};

		var buildNodeSelectionItems = function(nodeIds) {
			var items = [
				{ code: TOTAL_LABEL, description: TOTAL_LABEL }
			];
			(nodeIds || []).forEach(function(nodeId) {
				items.push({
					code: nodeId,
					description: nodeId
				});
			});
			return items;
		};

		var aggregatePageByNode = function(rows, selectedNode) {
			var aggregates = buildPageAggregates(rows);
			var selection = selectedNode || TOTAL_LABEL;
			var valuesByKey = selection === TOTAL_LABEL
				? aggregates.grandTotals
				: (aggregates.totalsByNode[selection] || {});

			var backgroundColors = [];
			var borderColors = [];
			var data = aggregates.statKeys.map(function(key, index) {
				var color = DATASET_COLORS[index % DATASET_COLORS.length];
				backgroundColors.push(color);
				borderColors.push(color);
				return valuesByKey[key] || 0;
			});

			return {
				labels: aggregates.statKeys,
				datasets: [
					{
						label: selection,
						data: data,
						backgroundColor: backgroundColors,
						borderColor: borderColors,
						borderWidth: 2
					}
				],
				nodeIds: aggregates.nodeIds
			};
		};

		var findPageTimeRange = function(rows) {
			var minTime = null;
			var maxTime = null;
			(rows || []).forEach(function(row) {
				var eventTime = TimeUtil.parseDate(row.eventTime);
				if (!eventTime) {
					return;
				}
				var millis = eventTime.getTime();
				if (minTime === null || millis < minTime) {
					minTime = millis;
				}
				if (maxTime === null || millis > maxTime) {
					maxTime = millis;
				}
			});
			return {
				start: minTime != null ? new Date(minTime) : null,
				end: maxTime != null ? new Date(maxTime) : null
			};
		};

		var buildChartConfig = function(aggregated, chartType) {
			var selectedChartType = chartType || CHART_TYPE_BAR;
			var isLine = selectedChartType === CHART_TYPE_LINE || selectedChartType === CHART_TYPE_FILL_LINE;
			var datasets = (aggregated.datasets || []).map(function(dataset) {
				var chartDataset = Object.assign({}, dataset);
				if (isLine) {
					chartDataset.tension = 0.2;
					chartDataset.fill = selectedChartType === CHART_TYPE_FILL_LINE;
				}
				return chartDataset;
			});

			return {
				type: isLine ? 'line' : 'bar',
				data: {
					labels: aggregated.labels || [],
					datasets: datasets
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: {
							display: true,
							position: 'bottom'
						}
					},
					scales: {
						y: {
							beginAtZero: true,
							title: {
								display: true,
								text: 'Stat Value'
							}
						},
						x: {
							ticks: {
								maxRotation: 45,
								minRotation: 0,
								autoSkip: true
							}
						}
					}
				}
			};
		};

		var toolbar = ElementUtil.create({
			tag: 'dl-toolbar',
			slot: 'toolbar',
			theme: 'spacing',
			children: [
				{
					tag: 'vaadin-button',
					itemid: 'refresh',
					'class': 'spacer-toolbar-item',
					children:[
						ElementUtil.makeIcon('vaadin:refresh', ' Refresh')
					], 
					listeners: {
						click: function() {
							actionRefresh();
						}
					}
				},
				{
					tag: 'span',
					innerHTML: '|'
				},
				{										
					tag: 'vaadin-combo-box',
					itemid: 'filterType',
					'class': 'spacer-toolbar-item',
					'keep-open': true,					
					style: 'width: 250px;',
					label: 'Type',
					'item-value-path': 'code',
					'item-label-path': 'description',
					required: true,
					postinit: function(combo) {
						Service.getLookupService().getLookupValues('SystemStatisticType').then(function(data){
							combo.items = data;
						});
					},
					placeHolder: 'Select',
					listeners: {
						change: function() {							
							actionRefresh();
						}
					}					
				},
				{
					tag: 'vaadin-date-picker',
					'class': 'spacer-toolbar-item',
					itemid: 'filterStartDate',
					label: 'Filter Start Date',												
					name: 'filterStartDate',
					value: function() {
						return TimeUtil.formatDateForTimeField(TimeUtil.startCurrentMonth());	
					}()
				},
				{
					tag: 'vaadin-date-picker',
					'class': 'spacer-toolbar-item',
					itemid: 'filterEndDate',
					label: 'Filter End Date',												
					name: 'filterEndDate',
					value: function() {
						return TimeUtil.formatDateForTimeField(TimeUtil.endCurrentMonth());	
					}()
				},
				{
					tag: 'vaadin-button',
					itemid: 'visualize',
					'class': 'spacer-toolbar-item',
					style: 'float: right; margin-top: 35px;',
					children:[
						ElementUtil.makeIcon('vaadin:chart', ' Visualize')
					],
					listeners: {
						click: function() {
							actionShowVisualization();
						}
					}
				}
			]
		});		
		
		var grid = ElementUtil.create({
			tag: 'vaadin-grid',
			theme: 'row-stripes column-borders wrap-cell-content',
			'column-reordering-allowed': '',
			style: 'height: 100%;',			
			slot: 'primary',
			defaults: {
				tag: 'vaadin-grid-sort-column',
				resizable: true,
				listeners: {
					'direction-changed' : function(event) {
						if (event.detail && event.detail.value) {
							grid.currentSortField = event.target.path;
							grid.currentSortDirection = event.detail.value.toUpperCase();
							pagingToolbar.loadCurrentPage();
						}
					}
				}
			},			
			children: [
				{						
					path: 'eventTime',
					header: 'Event Date',
					width: '5em',					
					renderer: function(root, column, rowData) {
						var date = TimeUtil.parseDate(rowData.item.eventTime);						
						root.innerHTML = TimeUtil.formatDateStandard(date);						
					}
				},
				{						
					path: 'nodeId',
					header: 'Node Id',
					width: '5em'
				},
				{						
					path: 'expirationDate',
					header: 'Expiration Date',
					width: '5em',					
					renderer: function(root, column, rowData) {
						var date = TimeUtil.parseDate(rowData.item.expirationDate);						
						root.innerHTML = TimeUtil.formatDateStandard(date);						
					}
				},								
				{						
					path: 'stats',
					header: 'Stats',
					'text-align': 'center',
					width: '12em',
					'flex-grow': '2',
					renderer: function(root, column, rowData) {
						var stats = rowData.item.stats;
						var display = [];
						if (stats) {
							for (var key in stats) {
								display.push('<b>' + key + ':</b> ' + stats[key]);
							}
						}						
						root.innerHTML = display.join('<br>');
					}
				}
			],
			listeners: {
				'active-item-changed': function(event) {
					const item = event.detail.value;
					grid.selectedItems = item ? [item] : [];
					
					if (grid.selectedItems.length > 0) {					  
											
					} else {						

					}
				}
			}
		});	
			
		var pagingToolbar = ElementUtil.create({
			tag: 'dl-paging-toolbar',
			pageSize: 100,
			postinit: function(element) {
				element.grid = grid;
				element.loader = function(backingGrid, pageNumber, pagingToolbar) {
					var pageRange = pagingToolbar.pullRange();
					var loadMask = grid.showLoading();
										
					var loadData = function() {
						grid.currentFilter = buildCurrentFilter(pageRange, backingGrid);

						var successHandler = function(records) {
							loadMask.remove();
														
							grid.activeItem = null;						
							grid.items = records.data;

							pagingToolbar.totalRecords = records.totalNumber;
							pagingToolbar.updatePager();						
						};
						
						Service.getApplicationService().getSystemStats(grid.currentFilter).then(function(records){
							successHandler(records);
						});						
					};	
					
					loadData();
														
				};
			},
			listeners: {
				ready: function() {					
					//pagingToolbar.loadCurrentPage();					
				}
			}
			
		});

		var footer = ExportUtil.createExportFooter({
			pagingToolbar: pagingToolbar,
			grid: grid,
			itemid: 'primary',
			onDetail: function() {
				var exportFilter = Object.assign({}, grid.currentFilter || {});
				delete exportFilter.offset;
				delete exportFilter.max;
				Service.getApplicationService().exportSystemStats(exportFilter);
			}
		});
		
		var flexLayout = ElementUtil.create({
			tag: 'dl-flex-layout',
			children: [
				toolbar,
				grid,
				footer.footerContainer
			]
		});		
		statGrid.append(flexLayout);		
	
		var actionRefresh = function() {
			
			var filterType = ElementUtil.query(toolbar, 'filterType');
			if (filterType.value) {
				pagingToolbar.loadPage(1);	
								
			} else {
				window.DL.MessageUtil.showToast('Validation', 'Type must be selected', {									
					theme: 'error'
				});				
			}			
		};

		var actionShowVisualization = function() {
			var filterType = ElementUtil.query(toolbar, 'filterType');
			if (!filterType.value) {
				window.DL.MessageUtil.showToast('Validation', 'Type must be selected', {
					theme: 'error'
				});
				return;
			}

			var chartWin = ElementUtil.create({
				tag: 'dl-dialog',
				title: 'System Statistics',
				icon: 'vaadin:chart',
				scope: 'content',
				width: .75,
				height: .75,
				fillContent: true,
				listeners: {
					ready: function() {
						chartWin.show();

						var currentPageRows = [];
						var selectedChartType = CHART_TYPE_BAR;
						var selectedNode = TOTAL_LABEL;
						var chartPanel = null;
						var nodeCombo = null;

						var startLabel = ElementUtil.create({
							tag: 'span',
							itemid: 'dataStartLabel',
							style: 'margin-right: 16px;',
							innerHTML: 'Start: —'
						});
						var endLabel = ElementUtil.create({
							tag: 'span',
							itemid: 'dataEndLabel',
							innerHTML: 'End: —'
						});

						var updateTimeRangeLabels = function(rows) {
							var timeRange = findPageTimeRange(rows);
							startLabel.textContent = 'Start: ' + (timeRange.start ? TimeUtil.formatDateStandard(timeRange.start) : '—');
							endLabel.textContent = 'End: ' + (timeRange.end ? TimeUtil.formatDateStandard(timeRange.end) : '—');
						};

						var syncNodeSelection = function(nodeIds) {
							if (!nodeCombo) {
								return;
							}
							var items = buildNodeSelectionItems(nodeIds);
							nodeCombo.items = items;
							var selectionStillValid = items.some(function(item) {
								return item.code === selectedNode;
							});
							if (!selectionStillValid) {
								selectedNode = TOTAL_LABEL;
							}
							if (nodeCombo.value !== selectedNode) {
								nodeCombo.value = selectedNode;
							}
						};

						var refreshChart = function() {
							if (!chartPanel) {
								return;
							}
							var aggregated = aggregatePageByNode(currentPageRows, selectedNode);
							syncNodeSelection(aggregated.nodeIds);
							chartPanel.updateChart(buildChartConfig(aggregated, selectedChartType));
						};

						nodeCombo = ElementUtil.create({
							tag: 'vaadin-combo-box',
							itemid: 'nodeSelection',
							'class': 'spacer-toolbar-item',
							style: 'width: 220px;',
							label: 'Node',
							'item-value-path': 'code',
							'item-label-path': 'description',
							postinit: function(combo) {
								combo.items = buildNodeSelectionItems([]);
								combo.value = TOTAL_LABEL;
							},
							listeners: {
								change: function() {
									var nextSelection = nodeCombo.value || TOTAL_LABEL;
									if (nextSelection === selectedNode) {
										return;
									}
									selectedNode = nextSelection;
									refreshChart();
								}
							}
						});

						var chartTypeCombo = ElementUtil.create({
							tag: 'vaadin-combo-box',
							itemid: 'chartType',
							'class': 'spacer-toolbar-item',
							style: 'width: 160px;',
							label: 'Chart Type',
							'item-value-path': 'code',
							'item-label-path': 'description',
							postinit: function(combo) {
								combo.items = [
									{ code: CHART_TYPE_BAR, description: 'Bar' },
									{ code: CHART_TYPE_LINE, description: 'Line' },
									{ code: CHART_TYPE_FILL_LINE, description: 'Fill Line' }
								];
								combo.value = CHART_TYPE_BAR;
							},
							listeners: {
								change: function() {
									selectedChartType = chartTypeCombo.value || CHART_TYPE_BAR;
									refreshChart();
								}
							}
						});

						var dialogToolbar = ElementUtil.create({
							tag: 'dl-toolbar',
							theme: 'spacing',
							children: [
								nodeCombo,
								chartTypeCombo,
								startLabel,
								endLabel
							]
						});

						chartPanel = ElementUtil.create({
							tag: 'dl-chart-panel',
							style: 'flex: 1 1 auto; min-height: 320px; width: 100%;',
							listeners: {
								ready: function() {
									refreshChart();
								}
							}
						});

						var chartBackingGrid = {
							currentSortField: grid.currentSortField,
							currentSortDirection: grid.currentSortDirection
						};

						var chartPagingToolbar = ElementUtil.create({
							tag: 'dl-paging-toolbar',
							pageSize: 100,
							postinit: function(element) {
								element.grid = chartBackingGrid;
								element.loader = function(backingGrid, pageNumber, chartPager) {
									var pageRange = chartPager.pullRange();
									var filter = buildCurrentFilter(pageRange, backingGrid);
									Service.getApplicationService().getSystemStats(filter).then(function(records) {
										currentPageRows = records.data || [];
										chartPager.totalRecords = records.totalNumber;
										chartPager.updatePager();
										updateTimeRangeLabels(currentPageRows);
										refreshChart();
									});
								};
							},
							listeners: {
								ready: function() {
									var startingPage = pagingToolbar.currentPage || 1;
									chartPagingToolbar.loadPage(startingPage);
								}
							}
						});

						var contentLayout = ElementUtil.create({
							tag: 'div',
							style: 'display: flex; flex-direction: column; height: 100%; width: 100%; gap: 8px; box-sizing: border-box; padding: 8px;',
							children: [
								dialogToolbar,
								chartPanel,
								chartPagingToolbar
							]
						});

						chartWin.addToContentElement(contentLayout);
					}
				}
			});
		};
				
	}
	
}
customElements.define('dl-statistics-grid', SystemStatsGrid);
